import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'

export default class extends Controller {
  static values = {
    alertsUrl: String
  }

  connect() {
    this.fetchAndDisplayAlert()
  }

  async fetchAndDisplayAlert() {
    try {
      const currentPath = window.location.pathname
      const url = new URL(this.alertsUrlValue, window.location.origin)
      url.searchParams.set('path', currentPath)

      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.has('preview')) {
        url.searchParams.set('preview', urlParams.get('preview'))
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`Behold: Failed to fetch alerts: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data || !data.alerts || data.alerts.length === 0) {
        return
      }

      const alertToShow = data.alerts.find(alert => this.shouldShowAlert(alert))

      if (alertToShow) {
        this.showAlert(alertToShow)
        this.incrementImpressionCount(alertToShow.alert_key)
      }
    } catch (error) {
      console.error('Behold: Error fetching alerts:', error)
    }
  }

  shouldShowAlert(alert) {
    if (this.isForceMode()) {
      return true
    }

    const storage = this.getAlertStorage(alert.alert_key)

    if (storage.dismissed) {
      return false
    }

    if (storage.impressions >= alert.max_impressions) {
      return false
    }

    return true
  }

  isForceMode() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.has('preview') && urlParams.get('preview') !== ''
  }

  async showAlert(alert) {
    const html = await this.fetchAlertHtml(alert.alert_key)

    if (alert.alert_type === 'banner') {
      this.showBanner(html, alert.alert_key)
    } else {
      this.showModal(html, alert.alert_key)
    }
  }

  showModal(html, alertKey) {
    document.body.insertAdjacentHTML('beforeend', html)

    const modal = document.getElementById('behold-modal')
    const bsModal = new Modal(modal)
    bsModal.show()

    modal.addEventListener('hidden.bs.modal', () => {
      modal.remove()
    })

    const dismissBtn = modal.querySelector('[data-action="click->behold#dismiss"]')
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        this.dismissModal(alertKey, bsModal)
      })
    }

    const links = modal.querySelectorAll('a')
    links.forEach(link => {
      link.addEventListener('click', () => {
        this.markAsDismissed(alertKey)
      })
    })
  }

  showBanner(html, alertKey) {
    document.body.insertAdjacentHTML('afterbegin', html)

    const banner = document.getElementById('behold-banner')
    if (!banner) {
      console.error('Behold: Banner element not found after insertion')
      return
    }

    const dismissBtn = banner.querySelector('[data-action="click->behold#dismiss"]')
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        this.dismissBanner(alertKey, banner)
      })
    }

    const links = banner.querySelectorAll('a')
    links.forEach(link => {
      link.addEventListener('click', () => {
        this.markAsDismissed(alertKey)
      })
    })
  }

  async fetchAlertHtml(alertKey) {
    const url = new URL(`${this.alertsUrlValue}/${alertKey}`, window.location.origin)

    const response = await fetch(url, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      console.error('Behold: Failed to fetch alert HTML:', response.statusText)
      return ''
    }

    return await response.text()
  }

  dismissModal(alertKey, bsModal) {
    this.markAsDismissed(alertKey)
    bsModal.hide()
  }

  dismissBanner(alertKey, banner) {
    this.markAsDismissed(alertKey)
    banner.remove()
  }

  getAlertStorage(alertKey) {
    const storageKey = `behold_${alertKey}`
    const stored = localStorage.getItem(storageKey)

    if (stored) {
      return JSON.parse(stored)
    }

    return {
      dismissed: false,
      impressions: 0,
      lastShown: null
    }
  }

  incrementImpressionCount(alertKey) {
    const storage = this.getAlertStorage(alertKey)
    storage.impressions += 1
    storage.lastShown = new Date().toISOString()

    const storageKey = `behold_${alertKey}`
    localStorage.setItem(storageKey, JSON.stringify(storage))
  }

  markAsDismissed(alertKey) {
    const storage = this.getAlertStorage(alertKey)
    storage.dismissed = true

    const storageKey = `behold_${alertKey}`
    localStorage.setItem(storageKey, JSON.stringify(storage))
  }

}
